import React, { useEffect, useRef, useState } from 'react';
import {
  Layers, Users, Activity, Sliders, Plus, Trash2,
  Search, CheckCircle, GripVertical, ArrowUp, ArrowDown,
  RefreshCw, Palette,
} from 'lucide-react';
import {
  CanvasElement, ColumnDefinition, PageSizePreset, ClientAccount,
} from '../types';
import { lookupBarcode, saveProductToBackend } from '../utils/barcodeLookup';
import { SERIAL_TYPES } from '../utils/invoiceNumber';
import {
  isInvoiceMobileEmbed,
  sendInvoiceMobileAction,
} from '../utils/invoiceMobileBridge';
const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
// ─── Types ────────────────────────────────────────────────────────────────────

interface RightSidebarProps {
  // tab
  rightSidebarTab: 'layers' | 'clients' | 'logs' | 'settings';
  setRightSidebarTab: (v: 'layers' | 'clients' | 'logs' | 'settings') => void;

  // elements
  elements: CanvasElement[];
  selectedElementId: string | null;
  setSelectedElementId: (v: string | null) => void;
  selectedElement: CanvasElement | null;
  lockedElementIds: string[];
  setLockedElementIds: React.Dispatch<React.SetStateAction<string[]>>;

  // element actions
  handleDeleteElement: (id: string) => void;
  handleDuplicateElement: (id: string) => void;
  handleZIndex: (id: string, action: 'front' | 'back' | 'up' | 'down') => void;
  updateElementProps: (id: string, props: Partial<CanvasElement>) => void;
  commitCurrentState: () => void;
  saveToHistory: (els: CanvasElement[], ps: PageSizePreset) => void;
  pageSize: PageSizePreset;

  // inline edit
  inlineEditingId: string | null;
  handleInlineEditSave: () => void;

  // serial
  triggerIncrementSerial: (el: CanvasElement) => void;
  formatSerialNumber: (settings: any) => string;
  authUserId: string;

  // clients
  accounts: ClientAccount[];
  rightClientSearch: string;
  setRightClientSearch: (v: string) => void;
  handleUseAccountInCreator: (act: ClientAccount) => void;

  // logs
  logs: any[];

  // financial helpers
  computeTableTotals: (el: CanvasElement) => { subtotal: number; tax: number; discount: number; shipping: number; total: number };
  activeIndustryGroup: string;
  activeIndustryName: string;
  getFinancialSuggestions: (group: string) => { taxLabel: string; taxRate: number; discountLabel: string; discountRate: number; description: string }[];
  setLogs: (v: any) => void;
  addLogItem: (logs: any[], action: string, details: string) => any[];

  // column drag state
  draggedColumnIdx: number | null;
  setDraggedColumnIdx: (v: number | null) => void;
  draggedOverColumnIdx: number | null;
  setDraggedOverColumnIdx: (v: number | null) => void;

  // bilingual
  bilingualMode: boolean;
}


// ─── Component ────────────────────────────────────────────────────────────────

const RightSidebar: React.FC<RightSidebarProps> = ({
  rightSidebarTab, setRightSidebarTab,
  elements, selectedElementId, setSelectedElementId, selectedElement,
  lockedElementIds, setLockedElementIds,
  handleDeleteElement, handleDuplicateElement, handleZIndex,
  updateElementProps, commitCurrentState, saveToHistory, pageSize,
  inlineEditingId, handleInlineEditSave,
  triggerIncrementSerial, formatSerialNumber, authUserId,
  accounts, rightClientSearch, setRightClientSearch, handleUseAccountInCreator,
  logs,
  computeTableTotals, activeIndustryGroup, activeIndustryName,
  getFinancialSuggestions, setLogs, addLogItem,
  draggedColumnIdx, setDraggedColumnIdx, draggedOverColumnIdx, setDraggedOverColumnIdx,
  bilingualMode,
}) => {
  const mobileEmbed = isInvoiceMobileEmbed();

  const [sidebarWidth, setSidebarWidth] = useState(420);
  const isResizing = useRef(false);

  const mobileUpdateElementProps = (
  id: string,
  props: Partial<CanvasElement>,
) => {
  if (mobileEmbed) {
    sendInvoiceMobileAction({
      action: 'update-element-props',
      elementId: id,
      props,
    });
  }

  // Always update local state too — the Text Content textarea is a
  // controlled input bound to local `elements`. Skipping this when the
  // RN send succeeds left the field snapping back to the stale value
  // on every keystroke, breaking typing/backspace.
  updateElementProps(id, props);
};

const mobileDeleteElement = (id: string) => {
  if (mobileEmbed) {
    const sent = sendInvoiceMobileAction({
      action: 'delete-element',
      elementId: id,
    });

    if (sent) return;
  }

  handleDeleteElement(id);
};

const mobileZIndex = (
  id: string,
  action: 'front' | 'back' | 'up' | 'down',
) => {
  if (mobileEmbed) {
    const sent = sendInvoiceMobileAction({
      action: 'z-index',
      elementId: id,
      zAction: action,
    });

    if (sent) return;
  }

  handleZIndex(id, action);
};

const mobileDuplicateElement = (id: string) => {
  if (mobileEmbed) {
    const sent = sendInvoiceMobileAction({
      action: 'duplicate-element',
      elementId: id,
    });

    if (sent) return;
  }

  handleDuplicateElement(id);
};

const mobileUseAccountInCreator = (act: ClientAccount) => {
  if (mobileEmbed) {
    const sent = sendInvoiceMobileAction({
      action: 'use-account-in-creator',
      account: act,
    });

    if (sent) return;
  }

  handleUseAccountInCreator(act);
};

const mobileIncrementSerial = (el: CanvasElement) => {
  if (mobileEmbed) {
    const sent = sendInvoiceMobileAction({
      action: 'increment-serial',
      elementId: el.id,
    });

    if (sent) return;
  }

  triggerIncrementSerial(el);
};

  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
  isResizing.current = true;
  e.preventDefault();
  const startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
  const startWidth = sidebarWidth;

  const onMouseMove = (e: MouseEvent | TouchEvent) => {
    if (!isResizing.current) return;
    const point = 'touches' in e ? e.touches[0] : e;
    if (!point) return;
    const delta = startX - point.clientX;
    const newWidth = Math.min(600, Math.max(260, startWidth + delta));
    setSidebarWidth(newWidth);
  };

  const onMouseUp = () => {
    isResizing.current = false;
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('touchmove', onMouseMove);
    window.removeEventListener('touchend', onMouseUp);
  };

  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('touchmove', onMouseMove, { passive: false });
  window.addEventListener('touchend', onMouseUp);
};

  const isSerialNoColumn = (col: ColumnDefinition) => {
    if (!col?.key || !col?.header) return false;
    const k = col.key.toLowerCase();
    const h = col.header.toLowerCase();
    return k === 'sno' || k === 'serial' || k === 'index' || h.includes('s.no') || h.includes('sl.no') || h.includes('sr.no') || h.includes('serial no') || h === 'no';
  };

  return (
    <>
    <style>{`
      @media (max-width: 768px) {
        .sidebar-resize-handle { display: none !important; }
        .right-sidebar-panel { width: 100% !important; }

      }
    `}</style>
    <aside
      className="right-sidebar-panel bg-white border-l border-slate-200 flex flex-col shrink-0 z-10 font-sans relative"
      style={{ 
        width: sidebarWidth,
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
  {/* Drag handle */}
  <div
    onMouseDown={handleMouseDown}
    onTouchStart={handleMouseDown}
    style={{
      position: 'absolute', left: 0, top: 0, bottom: 0, width: 6,
      cursor: 'col-resize', zIndex: 50,
    }}
    className="sidebar-resize-handle group hover:bg-blue-500/20 transition-colors"
  >
    <div style={{
      position: 'absolute', left: 2, top: '50%', transform: 'translateY(-50%)',
      width: 3, height: 48, borderRadius: 99, background: '#cbd5e1',
    }}
    className="group-hover:bg-blue-400 transition-colors"
    />
  </div>

      {/* Tab bar */}
      <div className="grid grid-cols-4 border-b border-slate-200 text-center text-xs shrink-0 bg-slate-50">
        {selectedElement && (
          <button onClick={() => setRightSidebarTab('settings')}
            className={`py-3.5 font-bold transition-all border-b-2 flex flex-col items-center gap-1 cursor-pointer ${rightSidebarTab === 'settings' ? 'text-blue-600 border-blue-600 bg-white' : 'text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-100/50'}`}>
            <Sliders className="w-4 h-4 text-blue-600" />
            <span>Settings</span>
          </button>
        )}
        {([
          { key: 'layers',  label: 'Layers',    Icon: Layers },
          { key: 'clients', label: 'Accounts',  Icon: Users },
          { key: 'logs',    label: 'Logs View', Icon: Activity },
        ] as const).map(({ key, label, Icon }) => (
          <button key={key} onClick={() => setRightSidebarTab(key)}
            className={`py-3.5 font-bold transition-all border-b-2 flex flex-col items-center gap-1 cursor-pointer ${rightSidebarTab === key ? 'text-blue-600 border-blue-600 bg-white' : 'text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-100/50'}`}>
            <Icon className="w-4 h-4 text-purple-700" />
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-visible p-4 space-y-4">

        {/* ── SETTINGS TAB ── */}
        {selectedElement && rightSidebarTab === 'settings' && (
          <SettingsPanel
            selectedElement={selectedElement}
            elements={elements}
            pageSize={pageSize}
            updateElementProps={mobileUpdateElementProps}
            commitCurrentState={commitCurrentState}
            saveToHistory={saveToHistory}
            handleDeleteElement={handleDeleteElement}
            handleZIndex={mobileZIndex}
            triggerIncrementSerial={triggerIncrementSerial}
            formatSerialNumber={formatSerialNumber}
            authUserId={authUserId}
            computeTableTotals={computeTableTotals}
            activeIndustryGroup={activeIndustryGroup}
            activeIndustryName={activeIndustryName}
            getFinancialSuggestions={getFinancialSuggestions}
            logs={logs}
            setLogs={setLogs}
            addLogItem={addLogItem}
            draggedColumnIdx={draggedColumnIdx}
            setDraggedColumnIdx={setDraggedColumnIdx}
            draggedOverColumnIdx={draggedOverColumnIdx}
            setDraggedOverColumnIdx={setDraggedOverColumnIdx}
            bilingualMode={bilingualMode}
            isSerialNoColumn={isSerialNoColumn}
          />
        )}

        {/* ── LAYERS TAB ── */}
        {rightSidebarTab === 'layers' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Layers</span>
              <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded font-bold font-mono">{elements.length} Elements</span>
            </div>
            <div className="space-y-2">
              {[...elements].sort((a, b) => b.zIndex - a.zIndex).map((el) => {
                const isSelected = selectedElementId === el.id;
                const isLocked = lockedElementIds.includes(el.id);
                return (
                  <div key={el.id} onClick={() => setSelectedElementId(el.id)}
                    className={`p-3 rounded-lg border transition-all text-xs flex flex-col gap-2 cursor-pointer relative ${isSelected ? 'bg-blue-50/70 border-blue-200 ring-1 ring-blue-500 shadow-xs' : 'bg-white border-slate-150 hover:bg-slate-50'}`}>
                    {isSelected && <div className="absolute top-2 right-2 w-1.5 h-1.5 bg-blue-600 rounded-full animate-ping" />}
                    <div className="flex items-start gap-2.5">
                      <div className={`p-1.5 rounded-md ${isSelected ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                        <Sliders className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-800 truncate capitalize">{el.type}</div>
                        <div className="text-[10px] text-slate-450 truncate font-medium mt-0.5">
                          {el.type === 'text' ? (el.textContent?.slice(0, 30) || 'Empty') :
                           el.type === 'table' ? `${el.rows?.length || 0} rows` :
                           el.type === 'stamp' ? el.stampText :
                           el.type === 'serial' ? formatSerialNumber(el.serialSettings) :
                           el.type}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-1 pt-2 border-t border-slate-100/80 text-[10px] font-semibold text-slate-500 gap-1">
                      <div className="flex items-center gap-1">
                        {(['front', 'up', 'down', 'back'] as const).map(action => (
                          <button key={action} onClick={(e) => { e.stopPropagation(); mobileZIndex(el.id, action); }}
                            className="px-1.5 py-0.5 bg-slate-100 hover:bg-blue-600 hover:text-white rounded transition-colors cursor-pointer text-[9px] font-bold capitalize">
                            {action === 'up' ? '▲' : action === 'down' ? '▼' : action}
                          </button>
                        ))}
                        <span className="text-[9px] text-slate-400 font-bold ml-1 font-mono">Z:{el.zIndex}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={(e) => { e.stopPropagation(); setLockedElementIds(prev => isLocked ? prev.filter(id => id !== el.id) : [...prev, el.id]); }}
                          className={`px-1.5 py-0.5 rounded border text-[9px] font-bold transition-colors cursor-pointer ${isLocked ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200'}`}>
                          {isLocked ? '🔒' : '🔓'}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); mobileDuplicateElement(el.id); }}
                          className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-600 hover:text-white rounded transition-colors flex items-center gap-0.5 cursor-pointer text-[9px] font-bold">
                          <Plus className="w-2 h-2" /><span>Copy</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); mobileDeleteElement(el.id); }}
                          className="p-1 hover:bg-red-50 text-slate-400 hover:text-red-650 rounded transition-colors cursor-pointer">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── CLIENTS TAB ── */}
        {rightSidebarTab === 'clients' && (
          <div className="space-y-3">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Quick Client Selector</div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input type="text" placeholder="Filter clients..." value={rightClientSearch} onChange={(e) => setRightClientSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div className="space-y-2">
              {accounts.filter(a => {
                if (!rightClientSearch) return true;
                const t = rightClientSearch.toLowerCase();
                return a.name.toLowerCase().includes(t) || (a.company && a.company.toLowerCase().includes(t)) || (a.email && a.email.toLowerCase().includes(t));
              }).map((act) => (
                <div key={act.id} className="p-3 bg-white border border-slate-150 rounded-lg text-xs hover:border-blue-100 transition-colors flex flex-col gap-2">
                  <div>
                    <div className="font-bold text-slate-800">{act.name}</div>
                    {act.company && <div className="text-[10px] text-purple-700 font-bold mt-0.5 uppercase tracking-wide">{act.company}</div>}
                    <div className="text-[10px] text-slate-450 font-mono mt-0.5 truncate">{act.email}</div>
                  </div>
                  <button onClick={() => mobileUseAccountInCreator(act)}
                    className="w-full py-1.5 bg-blue-50 border border-blue-100/50 hover:bg-blue-600 hover:text-white hover:border-transparent rounded-lg text-blue-700 text-[10px] font-bold uppercase transition-all cursor-pointer flex items-center justify-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5" /><span>Inject Into Design</span>
                  </button>
                </div>
              ))}
              {accounts.length === 0 && <div className="p-4 text-center text-slate-400 text-xs my-6">No profiles stored. Add some on the Accounts tab!</div>}
            </div>
          </div>
        )}

        {/* ── LOGS TAB ── */}
        {rightSidebarTab === 'logs' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-slate-500">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Live Audit Monitor</span>
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
            </div>
            <div className="space-y-2">
              {logs.slice(0, 10).map((log) => (
                <div key={log.id} className="p-2.5 bg-white border border-slate-150 rounded-lg text-xs space-y-1.5 shadow-xs">
                  <div className="flex items-center justify-between text-[9px] font-mono whitespace-nowrap gap-1">
                    <span className="text-[8.5px] px-1.5 py-0.5 rounded border border-blue-100 text-blue-700 font-extrabold uppercase bg-blue-50/50">{log.action}</span>
                    <span className="text-slate-400 font-bold">{new Date(log.timestamp).toLocaleTimeString()}</span>
                  </div>
                  <p className="text-[10px] text-slate-650 font-semibold leading-relaxed">{log.details}</p>
                </div>
              ))}
              {logs.length === 0 && <div className="p-4 text-center text-slate-400 text-xs my-6 font-medium">Logs clean. Actions are saved here in real-time.</div>}
            </div>
          </div>
        )}

      </div>
    </aside>
    </>
  );
};


const BarcodeScannerModal: React.FC<{
  onDetected: (value: string) => void;
  onClose: () => void;
}> = ({ onDetected, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState('Starting camera...');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [mode, setMode] = useState<'camera' | 'phone'>('camera');
  const [sessionId] = useState(() => Math.random().toString(36).slice(2, 10).toUpperCase());
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Phone mode — show QR + poll backend
  useEffect(() => {
    if (mode !== 'phone') return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${BACKEND}/scan-session/${sessionId}`);
        const data = await res.json();
        if (data.value) {
          clearInterval(pollRef.current!);
          onDetected(data.value);
        }
      } catch {}
    }, 800);
    return () => { if (pollRef.current) clearInterval(pollRef.current!); };
  }, [mode, sessionId]);

  // Camera mode — BarcodeDetector
  useEffect(() => {
    if (mode !== 'camera') return;
    let interval: ReturnType<typeof setInterval>;
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      .then(s => {
        setStream(s);
        if (videoRef.current) videoRef.current.srcObject = s;
        setStatus('Point camera at barcode or QR code...');
        // @ts-ignore
        if ('BarcodeDetector' in window) {
          // @ts-ignore
          const detector = new BarcodeDetector({
            formats: ['code_128', 'code_39', 'qr_code', 'ean_13', 'ean_8', 'data_matrix', 'pdf417', 'aztec']
          });
          interval = setInterval(async () => {
            if (!videoRef.current) return;
            try {
              const codes = await detector.detect(videoRef.current);
              if (codes.length > 0) {
                clearInterval(interval);
                s.getTracks().forEach(t => t.stop());
                setStatus(`✅ Detected: ${codes[0].rawValue}`);
                setTimeout(() => onDetected(codes[0].rawValue), 400);
              }
            } catch {}
          }, 300);
        } else {
          setStatus('Live scan not supported. Use manual paste or phone mode.');
        }
      })
      .catch(() => setStatus('⚠️ Camera denied. Use manual paste or phone mode.'));
    return () => {
      clearInterval(interval);
      stream?.getTracks().forEach(t => t.stop());
    };
  }, [mode]);

  const stopCamera = () => stream?.getTracks().forEach(t => t.stop());

  const phoneUrl = `${window.location.origin}/scan-session/${sessionId}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(phoneUrl)}`;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ 
        background: '#fff', 
        borderRadius: 16, 
        padding: 20, 
        width: 320, 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 12,
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Scan Serial / Barcode / QR</span>
          <button onClick={() => { stopCamera(); if (pollRef.current) clearInterval(pollRef.current); onClose(); }}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>✕</button>
        </div>

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: 6, background: '#f1f5f9', borderRadius: 8, padding: 4 }}>
          <button onClick={() => { if (pollRef.current) clearInterval(pollRef.current); setMode('camera'); }}
            style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
              background: mode === 'camera' ? '#2563eb' : 'transparent',
              color: mode === 'camera' ? '#fff' : '#64748b' }}>
            📷 This Device
          </button>
          <button onClick={() => { stopCamera(); setMode('phone'); }}
            style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
              background: mode === 'phone' ? '#2563eb' : 'transparent',
              color: mode === 'phone' ? '#fff' : '#64748b' }}>
            📱 Use Phone
          </button>
        </div>

        {/* Camera mode */}
        {mode === 'camera' && (
          <>
            <video ref={videoRef} autoPlay playsInline muted
              style={{ width: '100%', borderRadius: 10, border: '2px solid #3b82f6', aspectRatio: '4/3', objectFit: 'cover', background: '#000' }} />
            <p style={{ fontSize: 11, color: '#64748b', textAlign: 'center', fontWeight: 500, margin: 0 }}>{status}</p>
          </>
        )}

        {/* Phone mode */}
        {mode === 'phone' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <p style={{ fontSize: 11, color: '#64748b', textAlign: 'center', margin: 0, fontWeight: 500 }}>
              Scan this QR with your phone — it opens the scanner page on your phone camera:
            </p>
            <img src={qrSrc} alt="scan QR" style={{ width: 160, height: 160, borderRadius: 8, border: '2px solid #e2e8f0' }} />
            <code style={{ fontSize: 9, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', wordBreak: 'break-all', textAlign: 'center', color: '#334155' }}>
              {phoneUrl}
            </code>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#22c55e', fontSize: 11, fontWeight: 600 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
              Waiting for phone to scan...
            </div>
          </div>
        )}

        {/* Manual paste — always visible */}
        <input
          type="text"
          placeholder="Or type / paste serial here, press Enter"
          style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', fontSize: 11, fontFamily: 'monospace', outline: 'none', width: '100%', boxSizing: 'border-box' }}
          onKeyDown={(e) => {
            const val = (e.target as HTMLInputElement).value.trim();
            if (e.key === 'Enter' && val) {
              stopCamera();
              if (pollRef.current) clearInterval(pollRef.current);
              onDetected(val);
            }
          }}
          autoFocus={mode === 'phone'}
        />
        <p style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', margin: 0 }}>
          USB/Bluetooth scanners also work — focus this input and scan
        </p>

      </div>
    </div>
  );
};

const COUNTRY_TAXES = [
  { country: '🇮🇳 India', taxes: [{ label: 'GST', rate: 18 }], discounts: [] },
  { country: '🇦🇪 UAE', taxes: [{ label: 'VAT', rate: 5 }], discounts: [] },
  { country: '🇬🇧 UK', taxes: [{ label: 'VAT', rate: 20 }], discounts: [] },
  { country: '🇺🇸 USA (CA)', taxes: [{ label: 'Sales Tax', rate: 8.25 }], discounts: [] },
  { country: '🇺🇸 USA (NY)', taxes: [{ label: 'Sales Tax', rate: 8.875 }], discounts: [] },
  { country: '🇦🇺 Australia', taxes: [{ label: 'GST', rate: 10 }], discounts: [] },
  { country: '🇨🇦 Canada', taxes: [{ label: 'GST', rate: 5 }, { label: 'HST', rate: 8 }], discounts: [] },
  { country: '🇩🇪 Germany', taxes: [{ label: 'VAT', rate: 19 }], discounts: [] },
  { country: '🇫🇷 France', taxes: [{ label: 'VAT', rate: 20 }], discounts: [] },
  { country: '🇸🇬 Singapore', taxes: [{ label: 'GST', rate: 9 }], discounts: [] },
  { country: '🇳🇿 New Zealand', taxes: [{ label: 'GST', rate: 15 }], discounts: [] },
  { country: '🇿🇦 South Africa', taxes: [{ label: 'VAT', rate: 15 }], discounts: [] },
  { country: '🇧🇷 Brazil', taxes: [{ label: 'ICMS', rate: 17 }], discounts: [] },
  { country: '🇲🇾 Malaysia', taxes: [{ label: 'SST', rate: 6 }], discounts: [] },
  { country: '🇵🇰 Pakistan', taxes: [{ label: 'GST', rate: 17 }], discounts: [] },
  { country: '🇧🇩 Bangladesh', taxes: [{ label: 'VAT', rate: 15 }], discounts: [] },
  { country: '🇳🇬 Nigeria', taxes: [{ label: 'VAT', rate: 7.5 }], discounts: [] },
  { country: '🇰🇪 Kenya', taxes: [{ label: 'VAT', rate: 16 }], discounts: [] },
  { country: '🇯🇵 Japan', taxes: [{ label: 'Consumption Tax', rate: 10 }], discounts: [] },
  { country: '🇰🇷 South Korea', taxes: [{ label: 'VAT', rate: 10 }], discounts: [] },
  { country: '🇨🇳 China', taxes: [{ label: 'VAT', rate: 13 }], discounts: [] },
  { country: '🇮🇩 Indonesia', taxes: [{ label: 'PPN', rate: 11 }], discounts: [] },
  { country: '🇵🇭 Philippines', taxes: [{ label: 'VAT', rate: 12 }], discounts: [] },
  { country: '🇹🇭 Thailand', taxes: [{ label: 'VAT', rate: 7 }], discounts: [] },
  { country: '🇲🇽 Mexico', taxes: [{ label: 'IVA', rate: 16 }], discounts: [] },
  { country: '🇦🇷 Argentina', taxes: [{ label: 'IVA', rate: 21 }], discounts: [] },
  { country: '🇨🇭 Switzerland', taxes: [{ label: 'VAT', rate: 7.7 }], discounts: [] },
  { country: '🇳🇱 Netherlands', taxes: [{ label: 'VAT', rate: 21 }], discounts: [] },
  { country: '🇸🇦 Saudi Arabia', taxes: [{ label: 'VAT', rate: 15 }], discounts: [] },
  { country: '🇪🇬 Egypt', taxes: [{ label: 'VAT', rate: 14 }], discounts: [] },
];

const CountryTaxPicker: React.FC<{
  el: CanvasElement;
  updateElementProps: (id: string, props: any) => void;
  commitCurrentState: () => void;
  elements: CanvasElement[];
  pageSize: PageSizePreset;
  saveToHistory: (els: CanvasElement[], ps: PageSizePreset) => void;
}> = ({ el, updateElementProps, commitCurrentState, elements, pageSize, saveToHistory }) => {
  const [selected, setSelected] = useState('');

  const [editableTaxes, setEditableTaxes] = useState<{ label: string; rate: number }[]>([]);
  const selectedCountry = COUNTRY_TAXES.find(c => c.country === selected);

  const handleApply = () => {
    if (!selectedCountry) return;
    const updates = { taxes: selectedCountry.taxes, discounts: selectedCountry.discounts };
    updateElementProps(el.id, updates);
    saveToHistory(elements.map(e => e.id === el.id ? { ...e, ...updates } : e), pageSize);
    commitCurrentState();
  };

 return (
    <div className="bg-white border border-slate-200 rounded-lg p-2.5 space-y-2">
      <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">🌍 Country Tax Quick-Apply</p>
      <div className="flex gap-1.5">
        <select
          value={selected}
          onChange={(e) => {
            setSelected(e.target.value);
            const country = COUNTRY_TAXES.find(c => c.country === e.target.value);
            if (country) setEditableTaxes(country.taxes.map(t => ({ ...t })));
          }}
          className="flex-1 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-[10px] font-semibold text-slate-700 focus:outline-none focus:border-blue-400"
        >
          <option value="">Select country...</option>
          {COUNTRY_TAXES.map(c => (
            <option key={c.country} value={c.country}>{c.country}</option>
          ))}
        </select>
        <button
          onClick={() => {
            if (!editableTaxes.length) return;
            const updates = { taxes: editableTaxes, discounts: [] };
            updateElementProps(el.id, updates);
            saveToHistory(elements.map(e => e.id === el.id ? { ...e, ...updates } : e), pageSize);
            commitCurrentState();
          }}
          disabled={!selected}
          className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold disabled:opacity-40 cursor-pointer"
        >
          Apply
        </button>
      </div>

      {editableTaxes.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[8px] text-slate-400 font-bold uppercase tracking-wider">Edit before applying:</p>
          {editableTaxes.map((tax, idx) => (
            <div key={idx} className="flex items-center gap-1.5">
              <input
                type="text"
                value={tax.label}
                onChange={(e) => {
                  const updated = [...editableTaxes];
                  updated[idx] = { ...updated[idx], label: e.target.value };
                  setEditableTaxes(updated);
                }}
                className="flex-1 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-[10px] font-semibold text-slate-700 focus:outline-none focus:border-blue-400"
              />
              <input
                type="number"
                value={tax.rate}
                onChange={(e) => {
                  const updated = [...editableTaxes];
                  updated[idx] = { ...updated[idx], rate: parseFloat(e.target.value) || 0 };
                  setEditableTaxes(updated);
                }}
                className="w-14 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-[10px] font-mono text-center text-slate-700 focus:outline-none focus:border-blue-400"
              />
              <span className="text-[9px] text-slate-400">%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CustomTaxPresets: React.FC<{
  el: CanvasElement;
  updateElementProps: (id: string, props: any) => void;
  commitCurrentState: () => void;
  elements: CanvasElement[];
  pageSize: PageSizePreset;
  saveToHistory: (els: CanvasElement[], ps: PageSizePreset) => void;
}> = ({ el, updateElementProps, commitCurrentState, elements, pageSize, saveToHistory }) => {
  const [presets, setPresets] = useState<any[]>([]);
  const [presetName, setPresetName] = useState('');
  const [loading, setLoading] = useState(false);

  const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
  const token = localStorage.getItem('cloudairambo_jwt');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  useEffect(() => {
    fetch(`${BACKEND}/tax-presets/`, { headers })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setPresets(data); })
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!presetName.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/tax-presets/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: presetName.trim(),
          taxes: (el as any).taxes || [],
          discounts: (el as any).discounts || [],
        }),
      });
      const saved = await res.json();
      setPresets(prev => [saved, ...prev]);
      setPresetName('');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    await fetch(`${BACKEND}/tax-presets/${id}`, { method: 'DELETE', headers });
    setPresets(prev => prev.filter(p => p.id !== id));
  };

  const handleApply = (preset: any) => {
    const updates = { taxes: preset.taxes || [], discounts: preset.discounts || [] };
    updateElementProps(el.id, updates);
    saveToHistory(elements.map(e => e.id === el.id ? { ...e, ...updates } : e), pageSize);
    commitCurrentState();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-2.5 space-y-2">
      <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">My Saved Tax Presets</p>

      {/* Save current */}
      <div className="flex gap-1.5">
        <input
          type="text"
          placeholder="Preset name e.g. India GST 18%"
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
          className="flex-1 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-[10px] font-semibold text-slate-700 focus:outline-none focus:border-blue-400"
        />
        <button
          onClick={handleSave}
          disabled={loading || !presetName.trim()}
          className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-bold disabled:opacity-40 cursor-pointer"
        >
          Save
        </button>
      </div>

      {/* List */}
      <div className="space-y-1 max-h-[160px] overflow-y-auto">
        {presets.length === 0 && (
          <p className="text-[9px] text-slate-400 text-center py-2">No saved presets yet. Set taxes/discounts above then save.</p>
        )}
        {presets.map((preset) => (
          <div key={preset.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded p-1.5 gap-1">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-slate-800 truncate">{preset.name}</p>
              <p className="text-[8px] text-slate-400 font-mono">
                T: {(preset.taxes || []).map((t: any) => `${t.label} ${t.rate}%`).join(', ') || '—'} &nbsp;
                D: {(preset.discounts || []).map((d: any) => `${d.label} ${d.rate}%`).join(', ') || '—'}
              </p>
            </div>
            <button
              onClick={() => handleApply(preset)}
              className="px-1.5 py-0.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded text-[9px] font-bold cursor-pointer shrink-0"
            >
              Apply
            </button>
            <button
              onClick={() => handleDelete(preset.id)}
              className="p-0.5 text-slate-400 hover:text-red-500 cursor-pointer shrink-0"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
export default RightSidebar;

// ─── SettingsPanel (inner component, keeps file self-contained) ───────────────

interface SettingsPanelProps {
  selectedElement: CanvasElement;
  elements: CanvasElement[];
  pageSize: PageSizePreset;
  updateElementProps: (id: string, props: Partial<CanvasElement>) => void;
  commitCurrentState: () => void;
  saveToHistory: (els: CanvasElement[], ps: PageSizePreset) => void;
  handleDeleteElement: (id: string) => void;
  handleZIndex: (id: string, action: 'front' | 'back' | 'up' | 'down') => void;
  triggerIncrementSerial: (el: CanvasElement) => void;
  formatSerialNumber: (settings: any) => string;
  authUserId: string;
  computeTableTotals: (el: CanvasElement) => any;
  activeIndustryGroup: string;
  activeIndustryName: string;
  getFinancialSuggestions: (group: string) => any[];
  logs: any[];
  setLogs: (v: any) => void;
  addLogItem: (logs: any[], action: string, details: string) => any[];
  draggedColumnIdx: number | null;
  setDraggedColumnIdx: (v: number | null) => void;
  draggedOverColumnIdx: number | null;
  setDraggedOverColumnIdx: (v: number | null) => void;
  bilingualMode: boolean;
  isSerialNoColumn: (col: any) => boolean;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  selectedElement, elements, pageSize,
  updateElementProps, commitCurrentState, saveToHistory,
  handleDeleteElement, handleZIndex,
  triggerIncrementSerial, formatSerialNumber, authUserId,
  computeTableTotals, activeIndustryGroup, activeIndustryName,
  getFinancialSuggestions, logs, setLogs, addLogItem,
  draggedColumnIdx, setDraggedColumnIdx, draggedOverColumnIdx, setDraggedOverColumnIdx,
  bilingualMode, isSerialNoColumn,
}) => {
  const el = selectedElement;
  const [showScanner, setShowScanner] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

const showToast = (msg: string) => {
  setToast(msg);
  setTimeout(() => setToast(null), 3500);
};

  const save = (props: Partial<CanvasElement>) => {
    updateElementProps(el.id, props);
    saveToHistory(elements.map(e => e.id === el.id ? { ...e, ...props } : e), pageSize);
  };

  return (
    <div className="flex flex-col overflow-hidden">
      {toast && (
  <div style={{
    position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
    background: '#0f172a', color: '#fff', borderRadius: 12, padding: '10px 18px',
    fontSize: 12, fontWeight: 600, zIndex: 99999, whiteSpace: 'nowrap',
    boxShadow: '0 4px 24px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 8,
    border: '1px solid #1e293b'
  }}>
    <span style={{ fontSize: 16 }}>✅</span>
    {toast}
  </div>
)}
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between shrink-0">
        <span className="text-xs font-bold text-blue-600 uppercase tracking-widest font-mono flex items-center gap-1.5">
          <Sliders className="w-3.5 h-3.5" />{el.type} Settings
        </span>
        <button onClick={() => handleDeleteElement(el.id)} className="p-1 text-slate-400 hover:text-red-500 hover:bg-slate-100 rounded transition-colors"><Trash2 className="w-4 h-4" /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs text-slate-700">

        {/* Text / Stamp content */}
        {(el.type === 'text' || el.type === 'stamp') && (
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Text Content</label>
            <textarea
              value={el.type === 'stamp' ? (el.stampText ?? '') : (el.textContent ?? '')}
              onChange={(e) => updateElementProps(el.id, { [el.type === 'stamp' ? 'stampText' : 'textContent']: e.target.value })}
              onBlur={commitCurrentState}
             rows={el.type === 'stamp' ? 1 : 10}
className="w-full bg-white border border-slate-200 rounded-lg p-2.5 text-slate-800 focus:outline-none focus:border-blue-500 font-medium resize-y shadow-sm min-h-[200px] text-xs leading-relaxed"
            />

            {/* Dynamic Token Hint Banner with Click-to-Insert */}
            {el.type === 'text' && (
              <div className="mt-3 p-3 bg-gradient-to-br from-blue-50/90 to-indigo-50/50 border border-blue-200 rounded-xl text-xs space-y-2.5 shadow-xs">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 font-bold text-blue-700 text-[11px]">
                    <span>⚡ Auto-Generate Tags (Click to Insert)</span>
                  </div>
                  <span className="text-[9px] text-blue-500 font-semibold">Click tag to add</span>
                </div>

                <p className="text-[10px] text-slate-600 leading-relaxed">
                  Add these code words to your text. When generating or exporting an invoice, they will automatically be replaced with real data:
                </p>

                <div className="space-y-1.5">
                  {[
                    {
                      tag: '{client_details}',
                      title: 'Full Client Block',
                      desc: 'Auto-fills Name, Company, Full Address, Phone & Email',
                      color: 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100',
                    },
                    {
                      tag: '{client_name}',
                      title: 'Client Name Only',
                      desc: 'Auto-fills just the client or business name',
                      color: 'bg-teal-50 text-teal-700 border-teal-300 hover:bg-teal-100',
                    },
                    {
                      tag: '{invoice_number}',
                      title: 'Invoice / Record #',
                      desc: 'Auto-generates sequential number (e.g. INV-2026-0041)',
                      color: 'bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100',
                    },
                    {
                      tag: '{date}',
                      title: 'Issue Date',
                      desc: 'Auto-fills today\'s date (e.g. 2026-10-02)',
                      color: 'bg-indigo-50 text-indigo-700 border-indigo-300 hover:bg-indigo-100',
                    },
                    {
                      tag: '{due_date}',
                      title: 'Payment Due Date',
                      desc: 'Auto-fills payment deadline based on net terms',
                      color: 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100',
                    },
                    {
                      tag: '{staff_name}',
                      title: 'Staff / Issuer',
                      desc: 'Auto-fills logged-in user, clinician, or agent name',
                      color: 'bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100',
                    },
                  ].map(({ tag, title, desc, color }) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => {
                        const current = el.textContent || '';
                        const next = current ? `${current}\n${tag}` : tag;
                        updateElementProps(el.id, { textContent: next });
                        commitCurrentState();
                        showToast(`Added ${tag} to text`);
                      }}
                      className="w-full text-left p-1.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-2 transition-all cursor-pointer group"
                    >
                      <span className={`px-1.5 py-0.5 border rounded font-mono text-[9px] font-bold shrink-0 transition-colors ${color}`}>
                        {tag}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-[10px] text-slate-800 flex items-center justify-between">
                          <span>{title}</span>
                          <span className="text-[9px] text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity font-semibold">
                            + Insert
                          </span>
                        </div>
                        <p className="text-[9px] text-slate-500 leading-snug truncate">{desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* QR */}
      {el.type === 'qr' && (
  <div className="space-y-3">
    <div>
      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
        QR Value / URL
      </label>
      <input 
        type="text" 
        value={el.qrValue ?? ''} 
        onChange={(e) => updateElementProps(el.id, { qrValue: e.target.value })} 
        onBlur={commitCurrentState}
        className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800 font-mono shadow-sm text-xs" 
      />
    </div>

    {/* QR Caption Label Controls */}
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Caption Label</span>
        <label className="flex items-center gap-1.5 cursor-pointer text-[10px] font-bold text-slate-600">
          <input
            type="checkbox"
            checked={el.showLabel !== false}
            onChange={(e) => {
              updateElementProps(el.id, { showLabel: e.target.checked });
              commitCurrentState();
            }}
            className="rounded border-slate-300 text-blue-600 w-3.5 h-3.5"
          />
          <span>Show</span>
        </label>
      </div>

      {el.showLabel !== false && (
        <div className="space-y-2 pt-1 border-t border-slate-200">
          {/* Label Text Input (Multi-line Support) */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-[9px] font-bold text-slate-400 uppercase">Label Text</label>
              <span className="text-[8px] text-blue-500 font-semibold">Press Enter to stack lines</span>
            </div>
            <textarea
              rows={2}
              value={el.label ?? ''}
              placeholder={"Scan to\npay instantly"}
              onChange={(e) => updateElementProps(el.id, { label: e.target.value })}
              onBlur={commitCurrentState}
              className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs text-slate-800 font-medium focus:outline-none focus:border-blue-400 resize-none leading-tight"
            />
          </div>

          {/* Quick Standard QR Size Buttons (Allocates height for label) */}
          <div>
            <label className="text-[9px] font-bold text-slate-400 uppercase block mb-1">Standard Size</label>
            <div className="grid grid-cols-3 gap-1">
              {[
                { label: 'Compact (80)', w: 80, h: el.showLabel !== false ? 110 : 80 },
                { label: 'Standard (85)', w: 85, h: el.showLabel !== false ? 115 : 85 },
                { label: 'Large (100)', w: 100, h: el.showLabel !== false ? 135 : 100 },
              ].map(({ label: btnLabel, w, h }) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => {
                    updateElementProps(el.id, { width: w, height: h });
                    commitCurrentState();
                  }}
                  className={`py-1 rounded text-[10px] font-bold border transition-all ${
                    el.width === w
                      ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {btnLabel}
                </button>
              ))}
            </div>
          </div>

          {/* Position: Move Up / Down */}
          <div>
            <label className="text-[9px] font-bold text-slate-400 uppercase block mb-1">Label Position</label>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                type="button"
                onClick={() => {
                  updateElementProps(el.id, { labelPosition: 'top' });
                  commitCurrentState();
                }}
                className={`py-1 rounded text-[10px] font-bold border transition-all ${
                  el.labelPosition === 'top'
                    ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
              >
                ▲ Above QR
              </button>
              <button
                type="button"
                onClick={() => {
                  updateElementProps(el.id, { labelPosition: 'bottom' });
                  commitCurrentState();
                }}
                className={`py-1 rounded text-[10px] font-bold border transition-all ${
                  (el.labelPosition || 'bottom') === 'bottom'
                    ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
              >
                ▼ Below QR
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    <div className="bg-blue-50 border border-blue-100 rounded-lg p-2 text-[10px] text-blue-700 font-medium">
      📱 This QR lets clients scan and view the invoice on their phone.<br/>
      <span className="text-slate-400 mt-0.5 block">Auto-filled when you click <strong>Save & Track</strong>.</span>
    </div>
  </div>
)}

{/* IMAGE / LOGO */}
{el.type === 'image' && (
  <div className="space-y-2">
    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Logo / Image</label>

    {/* Preview */}
    {el.textContent && (
      <div className="w-full h-20 border border-slate-200 rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden">
        <img src={el.textContent} className="max-h-full max-w-full object-contain" />
      </div>
    )}

    {/* Upload */}
    <label className="flex items-center justify-center gap-2 w-full py-2.5 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
      <span className="text-xs font-bold text-blue-600">📁 Upload Logo</span>
      <input
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = () => {
            updateElementProps(el.id, { textContent: reader.result as string });
            commitCurrentState();
          };
          reader.readAsDataURL(file);
        }}
      />
    </label>

    {/* URL input */}
    <input
      type="text"
      placeholder="Or paste image URL..."
      className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-400"
      value={el.textContent?.startsWith('data:') ? '' : (el.textContent || '')}
      onChange={(e) => updateElementProps(el.id, { textContent: e.target.value })}
      onBlur={commitCurrentState}
    />

    {/* Remove */}
    {el.textContent && (
      <button
        onClick={() => { updateElementProps(el.id, { textContent: '' }); commitCurrentState(); }}
        className="w-full py-1.5 text-xs text-red-500 border border-red-200 rounded-lg hover:bg-red-50 font-bold transition-colors"
      >
        ✕ Remove Logo
      </button>
    )}
  </div>
)}
      
       {/* Serial */}
{el.type === 'serial' && el.serialSettings && (
  <div className="space-y-3 bg-slate-50 p-3 rounded-lg border border-slate-200">

    {/* Serial Type Picker */}
    <div>
      <label className="text-[9px] text-slate-400 uppercase font-bold block mb-1">Serial Type</label>
      <select
        value={el.serialSettings.serialType || 'product'}
        onChange={(e) => {
          const picked = SERIAL_TYPES.find(t => t.key === e.target.value);
          if (!picked) return;
          updateElementProps(el.id, { serialSettings: { ...el.serialSettings!, serialType: picked.key, prefix: picked.prefix, digits: picked.digits } });
          commitCurrentState();
        }}
        className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800 text-xs font-semibold focus:outline-none focus:border-blue-500"
      >
        {SERIAL_TYPES.map(t => (
          <option key={t.key} value={t.key}>{t.label} ({t.prefix})</option>
        ))}
      </select>
    </div>

    {/* Mode Toggle */}
    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-2">
      <span className="text-[9px] font-bold text-slate-400 uppercase">Mode:</span>
      <div className="flex gap-1 flex-1">
        <button onClick={() => updateElementProps(el.id, { serialSettings: { ...el.serialSettings!, manualMode: false } })}
          className={`flex-1 py-1 rounded text-[10px] font-bold transition-all ${!el.serialSettings.manualMode ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
          🔄 Auto
        </button>
        <button onClick={() => updateElementProps(el.id, { serialSettings: { ...el.serialSettings!, manualMode: true } })}
          className={`flex-1 py-1 rounded text-[10px] font-bold transition-all ${el.serialSettings.manualMode ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
          ✏️ Manual
        </button>
      </div>
    </div>

    {/* Manual Input */}
    {el.serialSettings.manualMode && (
      <div>
        <label className="text-[9px] text-slate-400 uppercase font-bold block mb-1">Manual Serial Value</label>
        <input type="text" value={el.serialSettings.manualValue || ''}
          onChange={(e) => updateElementProps(el.id, { serialSettings: { ...el.serialSettings!, manualValue: e.target.value } })}
          onBlur={commitCurrentState}
          placeholder="e.g. WRT-2026-CUSTOM-001"
          className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800 font-mono text-xs focus:outline-none focus:border-blue-500" />
      </div>
    )}

    {/* Scan to Detect */}
   {showScanner && (
  <BarcodeScannerModal
    onDetected={async (val) => {
  // 1. Fill the serial element
  updateElementProps(el.id, { serialSettings: { ...el.serialSettings!, manualMode: true, manualValue: val } });

  // 2. Lookup product info from public APIs
  const productInfo = await lookupBarcode(val);

  // 3. Inject into ALL tables
  const tableEls = elements.filter(e => e.type === 'table' && e.columns && e.rows);
  tableEls.forEach(tableEl => {
    if (tableEl.columns && tableEl.rows) {
      const targetCol = tableEl.columns.find(c =>
        c.colType === 'serial' ||
        ['serial', 'code', 'item', 'sku', 'barcode', 'product'].some(k =>
          c.key.toLowerCase().includes(k) || c.header.toLowerCase().includes(k)
        )
      ) || tableEl.columns.find(c =>
        c.key !== 'description' && c.key !== 'qty' && c.key !== 'rate' && c.key !== 'total'
      ) || tableEl.columns[0];

      const newRow = {
        id: `row-${Date.now()}-${tableEl.id}`,
        values: {
          description: productInfo
            ? `${productInfo.name}${productInfo.brand ? ' — ' + productInfo.brand : ''}`
            : val,
          [targetCol.key]: val,
          qty: 1,
          rate: productInfo?.rate || 0,
          total: productInfo?.rate || 0,
        }
      };
      updateElementProps(tableEl.id, { rows: [...tableEl.rows, newRow] });
    }
  });

  // Auto-save to your backend if found from public API (not already in your DB)
  if (productInfo && !productInfo.fromOwnDB) {
    saveProductToBackend(val, productInfo);
  }

  commitCurrentState();
  setShowScanner(false);
  showToast(
    productInfo
      ? `${productInfo.fromOwnDB ? '🏠 Your DB' : '🌐 Public API'}: ${productInfo.name}${productInfo.brand ? ' — ' + productInfo.brand : ''} → Added to table`
      : `Scanned: ${val} → Added to table`
  );
}}
    onClose={() => setShowScanner(false)}
  />
)}
    <button onClick={() => setShowScanner(true)}
      className="w-full py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-600 hover:text-white rounded-lg text-[10px] font-bold transition-all flex items-center justify-center gap-1.5">
      📷 Scan Barcode / QR Live
    </button>

    <div className="flex items-center justify-between">
      <span className="font-bold text-slate-750">Sequence Value:</span>
      <button onClick={() => triggerIncrementSerial(el)}
        className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded font-bold text-[10px] transition-colors flex items-center gap-1 shadow-sm">
        <RefreshCw className="w-3 h-3" /> Count Up
      </button>
    </div>
    <div className="text-center font-mono text-sm text-slate-700 bg-white border border-slate-200 rounded px-2 py-1">
      {el.serialSettings.manualMode && el.serialSettings.manualValue
        ? el.serialSettings.manualValue
        : formatSerialNumber(el.serialSettings)}
    </div>
    <div className="grid grid-cols-2 gap-2 text-[11px]">
      {[
        { label: 'Prefix',        field: 'prefix',  type: 'text'   },
        { label: 'Suffix',        field: 'suffix',  type: 'text'   },
        { label: 'Start Value',   field: 'current', type: 'number' },
        { label: 'Padding Count', field: 'digits',  type: 'number' },
      ].map(({ label, field, type }) => (
        <div key={field}>
          <label className="text-[9px] text-slate-400 uppercase font-bold">{label}</label>
          <input type={type} value={(el.serialSettings as any)[field]}
            onChange={(e) => updateElementProps(el.id, { serialSettings: { ...el.serialSettings!, [field]: type === 'number' ? parseInt(e.target.value) || 0 : e.target.value } })}
            onBlur={commitCurrentState}
            className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-slate-800 shadow-sm font-mono" />
        </div>
      ))}
    </div>
  </div>
)}

        {/* Signature */}
        {el.type === 'signature' && (
          <div className="space-y-3 bg-slate-50 p-3 rounded-lg border border-slate-200">
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Signature Name</label>
              <input type="text" value={el.signatureName || ''} onChange={(e) => updateElementProps(el.id, { signatureName: e.target.value })} onBlur={commitCurrentState}
                className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-850 font-medium text-xs" placeholder="e.g. Johnathan Doe" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Caption Label</label>
              <input type="text" value={el.textContent || ''} onChange={(e) => updateElementProps(el.id, { textContent: e.target.value })} onBlur={commitCurrentState}
                className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-850 font-medium text-xs" placeholder="e.g. Authorized Signature" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Font Style</label>
              <select value={el.fontFamily || 'Alex Brush'} onChange={(e) => save({ fontFamily: e.target.value })}
                className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800 focus:outline-none text-xs font-semibold">
                <option value="Alex Brush">Alex Brush (Elegant)</option>
                <option value="Dancing Script">Dancing Script (Playful)</option>
                <option value="Great Vibes">Great Vibes (Formal)</option>
                <option value="Sacramento">Sacramento (Modern)</option>
              </select>
            </div>
          </div>
        )}

        {/* Stamp options */}
        {el.type === 'stamp' && (
          <div className="space-y-2.5 bg-slate-50 p-3 rounded-lg border border-slate-200">
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Stamp Shape</label>
              <select value={el.stampShape || 'rectangle'} onChange={(e) => save({ stampShape: e.target.value as any })}
                className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800 text-xs font-semibold">
                <option value="rectangle">Classic Rectangle</option>
                <option value="circle">Official Circle</option>
                <option value="oval">Oval</option>
                <option value="pill">Pill / Capsule</option>
                <option value="badge">Rounded Badge</option>
              </select>
            </div>
            <div className="flex items-center gap-1.5 bg-white p-2 rounded border border-slate-150">
              <input type="checkbox" id="stampDraftCheck" checked={el.stampDraft ?? false}
                onChange={(e) => save({ stampDraft: e.target.checked })}
                className="rounded text-blue-600 w-3.5 h-3.5" />
              <label htmlFor="stampDraftCheck" className="text-xs font-semibold text-slate-705 cursor-pointer">Vintage distressed ink grains</label>
            </div>
          </div>
        )}

        {/* Table */}
        {el.type === 'table' && el.columns && el.rows && (
          <TableSettings
            el={el} elements={elements} pageSize={pageSize}
            updateElementProps={updateElementProps} commitCurrentState={commitCurrentState}
            saveToHistory={saveToHistory}
            computeTableTotals={computeTableTotals}
            activeIndustryGroup={activeIndustryGroup} activeIndustryName={activeIndustryName}
            getFinancialSuggestions={getFinancialSuggestions}
            logs={logs} setLogs={setLogs} addLogItem={addLogItem}
            draggedColumnIdx={draggedColumnIdx} setDraggedColumnIdx={setDraggedColumnIdx}
            draggedOverColumnIdx={draggedOverColumnIdx} setDraggedOverColumnIdx={setDraggedOverColumnIdx}
            isSerialNoColumn={isSerialNoColumn}
          />
        )}

    {/* Shape options — Photoshop level */}
{el.type === 'shape' && (
  <div className="space-y-2.5">

    {/* ── TRANSFORM ── */}
    <div className="bg-slate-50 p-2.5 rounded border border-slate-200 space-y-2">
      <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 block">Transform</span>
      <div className="grid grid-cols-4 gap-1">
        {([['X', 'x'], ['Y', 'y'], ['W', 'width'], ['H', 'height']] as const).map(([label, field]) => (
          <div key={field}>
            <label className="text-[9px] text-slate-400 font-bold block text-center">{label}</label>
            <input type="number" value={Math.round((el as any)[field] || 0)}
              onChange={(e) => {
                const v = parseInt(e.target.value) || 0;
                if ((field === 'width' || field === 'height') && el.lockAspectRatio) {
                  const ratio = (el.width || 1) / (el.height || 1);
                  if (field === 'width') updateElementProps(el.id, { width: v, height: Math.round(v / ratio) });
                  else updateElementProps(el.id, { height: v, width: Math.round(v * ratio) });
                } else {
                  updateElementProps(el.id, { [field]: v });
                }
              }}
              onBlur={commitCurrentState}
              className="w-full bg-white border border-slate-200 rounded px-1 py-1 text-slate-800 text-center font-mono text-[10px]" />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="text-[9px] text-slate-400 font-bold block mb-1">Rotate °</label>
          <input type="number" value={el.rotation || 0} min={-180} max={180}
            onChange={(e) => updateElementProps(el.id, { rotation: parseInt(e.target.value) || 0 })}
            onBlur={commitCurrentState}
            className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-slate-800 text-center font-mono text-[10px]" />
        </div>
        <div className="flex-1">
          <label className="text-[9px] text-slate-400 font-bold block mb-1">Opacity %</label>
          <input type="number" value={el.opacity || 100} min={0} max={100}
            onChange={(e) => updateElementProps(el.id, { opacity: parseInt(e.target.value) || 100 })}
            onBlur={commitCurrentState}
            className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-slate-800 text-center font-mono text-[10px]" />
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={() => save({ flipX: !el.flipX })}
          className={`flex-1 py-1 rounded border text-[10px] font-bold transition-all ${el.flipX ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
          ↔ Flip H
        </button>
        <button onClick={() => save({ flipY: !el.flipY })}
          className={`flex-1 py-1 rounded border text-[10px] font-bold transition-all ${el.flipY ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
          ↕ Flip V
        </button>
        <button onClick={() => save({ lockAspectRatio: !el.lockAspectRatio })}
          className={`flex-1 py-1 rounded border text-[10px] font-bold transition-all ${el.lockAspectRatio ? 'bg-amber-500 text-white border-amber-500' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
          {el.lockAspectRatio ? '🔒 Ratio' : '🔓 Ratio'}
        </button>
      </div>
    </div>

    {/* ── SHAPE TYPE ── */}
    <div className="bg-slate-50 p-2.5 rounded border border-slate-200 space-y-2">
      <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 block">Shape</span>
      <select value={el.shapeType || 'rectangle'} onChange={(e) => save({ shapeType: e.target.value as any })}
        className="w-full bg-white border border-slate-200 rounded p-1.5 font-semibold text-xs text-slate-700">
        <option value="rectangle">Rectangle / Box</option>
        <option value="circle">Circle / Ellipse</option>
        <option value="triangle">Triangle</option>
        <option value="star">5-Point Star</option>
        <option value="line">Horizontal Line</option>
        <option value="arrow">Block Arrow</option>
        <option value="diamond">Diamond</option>
        <option value="pentagon">Pentagon</option>
        <option value="hexagon">Hexagon</option>
        <option value="cross">Cross / Plus</option>
      </select>
    </div>

    {/* ── FILL ── */}
    <div className="bg-slate-50 p-2.5 rounded border border-slate-200 space-y-2">
      <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 block">Fill</span>
      <select value={el.fillType || 'solid'} onChange={(e) => save({ fillType: e.target.value as any })}
        className="w-full bg-white border border-slate-200 rounded p-1.5 font-semibold text-xs text-slate-700">
        <option value="solid">Solid</option>
        <option value="linear">Linear Gradient</option>
        <option value="radial">Radial Gradient</option>
        <option value="none">None / Transparent</option>
      </select>
      {(el.fillType === 'solid' || !el.fillType) && (
        <div className="flex gap-1.5 items-center bg-white p-1 rounded border border-slate-200">
          <input type="color" value={el.backgroundColor === 'transparent' ? '#ffffff' : (el.backgroundColor || '#ffffff')}
            onChange={(e) => updateElementProps(el.id, { backgroundColor: e.target.value })} onBlur={commitCurrentState}
            className="bg-transparent border-none w-7 h-7 cursor-pointer" />
          <span className="font-mono text-[10px] text-slate-800 font-semibold flex-1">{el.backgroundColor || '#ffffff'}</span>
        </div>
      )}
      {(el.fillType === 'linear' || el.fillType === 'radial') && (
        <div className="space-y-1.5">
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[9px] text-slate-400 font-bold block mb-1">From</label>
              <div className="flex gap-1 items-center bg-white p-1 rounded border border-slate-200">
                <input type="color" value={el.gradientFrom || '#ffffff'}
                  onChange={(e) => updateElementProps(el.id, { gradientFrom: e.target.value })} onBlur={commitCurrentState}
                  className="bg-transparent border-none w-6 h-6 cursor-pointer" />
                <span className="font-mono text-[9px] text-slate-700">{el.gradientFrom || '#fff'}</span>
              </div>
            </div>
            <div className="flex-1">
              <label className="text-[9px] text-slate-400 font-bold block mb-1">To</label>
              <div className="flex gap-1 items-center bg-white p-1 rounded border border-slate-200">
                <input type="color" value={el.gradientTo || '#000000'}
                  onChange={(e) => updateElementProps(el.id, { gradientTo: e.target.value })} onBlur={commitCurrentState}
                  className="bg-transparent border-none w-6 h-6 cursor-pointer" />
                <span className="font-mono text-[9px] text-slate-700">{el.gradientTo || '#000'}</span>
              </div>
            </div>
          </div>
          {el.fillType === 'linear' && (
            <div>
              <label className="text-[9px] text-slate-400 font-bold block mb-1">Angle: {el.gradientAngle || 0}°</label>
              <input type="range" min={0} max={360} value={el.gradientAngle || 0}
                onChange={(e) => updateElementProps(el.id, { gradientAngle: parseInt(e.target.value) })}
                onMouseUp={commitCurrentState}
                className="w-full accent-blue-600 h-1.5 rounded-lg cursor-pointer" />
            </div>
          )}
        </div>
      )}
    </div>

    {/* ── STROKE ── */}
    <div className="bg-slate-50 p-2.5 rounded border border-slate-200 space-y-2">
      <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 block">Stroke</span>
      <div className="flex gap-1.5 items-center bg-white p-1 rounded border border-slate-200">
        <input type="color" value={el.borderColor || el.color || '#000000'}
          onChange={(e) => updateElementProps(el.id, { borderColor: e.target.value })} onBlur={commitCurrentState}
          className="bg-transparent border-none w-7 h-7 cursor-pointer" />
        <span className="font-mono text-[10px] text-slate-800 font-semibold flex-1">{el.borderColor || el.color || '#000000'}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[9px] text-slate-400 font-bold block mb-1">Width (px)</label>
          <input type="number" value={el.borderWidth || 0} min={0} max={40}
            onChange={(e) => updateElementProps(el.id, { borderWidth: parseInt(e.target.value) || 0 })} onBlur={commitCurrentState}
            className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-slate-800 text-center font-mono text-[10px]" />
        </div>
        <div>
          <label className="text-[9px] text-slate-400 font-bold block mb-1">Align</label>
          <select value={el.strokeAlign || 'center'} onChange={(e) => save({ strokeAlign: e.target.value as any })}
            className="w-full bg-white border border-slate-200 rounded p-1 text-[10px] font-semibold text-slate-700">
            <option value="center">Center</option>
            <option value="inside">Inside</option>
            <option value="outside">Outside</option>
          </select>
        </div>
      </div>
      <div>
        <label className="text-[9px] text-slate-400 font-bold block mb-1">Style</label>
        <select value={el.lineStyle || 'solid'} onChange={(e) => save({ lineStyle: e.target.value as any })}
          className="w-full bg-white border border-slate-200 rounded p-1.5 text-[10px] font-semibold text-slate-700">
          <option value="solid">Solid</option>
          <option value="dashed">Dashed</option>
          <option value="dotted">Dotted</option>
          <option value="double">Double</option>
        </select>
      </div>
      {(el.lineStyle === 'dashed') && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[9px] text-slate-400 font-bold block mb-1">Dash (px)</label>
            <input type="number" value={el.dashSize || 8} min={1} max={40}
              onChange={(e) => updateElementProps(el.id, { dashSize: parseInt(e.target.value) || 8 })} onBlur={commitCurrentState}
              className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-slate-800 text-center font-mono text-[10px]" />
          </div>
          <div>
            <label className="text-[9px] text-slate-400 font-bold block mb-1">Gap (px)</label>
            <input type="number" value={el.gapSize || 4} min={1} max={40}
              onChange={(e) => updateElementProps(el.id, { gapSize: parseInt(e.target.value) || 4 })} onBlur={commitCurrentState}
              className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-slate-800 text-center font-mono text-[10px]" />
          </div>
        </div>
      )}
    </div>

    {/* ── CORNERS ── */}
    {el.shapeType !== 'line' && el.shapeType !== 'circle' && (
      <div className="bg-slate-50 p-2.5 rounded border border-slate-200 space-y-2">
        <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 block">Corners (px)</span>
        <div>
          <label className="text-[9px] text-slate-400 font-bold block mb-1">All corners</label>
          <input type="number" value={el.borderRadius || 0} min={0} max={200}
            onChange={(e) => {
              const v = parseInt(e.target.value) || 0;
              save({ borderRadius: v, borderRadiusTL: v, borderRadiusTR: v, borderRadiusBL: v, borderRadiusBR: v });
            }}
            className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-slate-800 text-center font-mono text-[10px]" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {([['TL', 'borderRadiusTL'], ['TR', 'borderRadiusTR'], ['BL', 'borderRadiusBL'], ['BR', 'borderRadiusBR']] as const).map(([label, field]) => (
            <div key={field}>
              <label className="text-[9px] text-slate-400 font-bold block mb-1">{label}</label>
              <input type="number" value={(el as any)[field] ?? el.borderRadius ?? 0} min={0} max={200}
                onChange={(e) => updateElementProps(el.id, { [field]: parseInt(e.target.value) || 0 })}
                onBlur={commitCurrentState}
                className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-slate-800 text-center font-mono text-[10px]" />
            </div>
          ))}
        </div>
      </div>
    )}

    {/* ── SHADOW ── */}
    <div className="bg-slate-50 p-2.5 rounded border border-slate-200 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-black uppercase tracking-wider text-slate-400">Shadow</span>
        <button onClick={() => save({ shadowEnabled: !el.shadowEnabled })}
          className={`px-2 py-0.5 rounded border text-[9px] font-bold transition-all ${el.shadowEnabled ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-200 text-slate-500'}`}>
          {el.shadowEnabled ? 'On' : 'Off'}
        </button>
      </div>
      {el.shadowEnabled && (
        <div className="space-y-2">
          <div className="flex gap-1.5 items-center bg-white p-1 rounded border border-slate-200">
            <input type="color" value={el.shadowColor || '#000000'}
              onChange={(e) => updateElementProps(el.id, { shadowColor: e.target.value })} onBlur={commitCurrentState}
              className="bg-transparent border-none w-7 h-7 cursor-pointer" />
            <span className="font-mono text-[10px] text-slate-800 flex-1">{el.shadowColor || '#000000'}</span>
            <input type="number" value={el.shadowOpacity ?? 40} min={0} max={100}
              onChange={(e) => updateElementProps(el.id, { shadowOpacity: parseInt(e.target.value) })} onBlur={commitCurrentState}
              className="w-10 bg-slate-50 border border-slate-200 rounded px-1 py-0.5 text-[10px] text-center font-mono" />
            <span className="text-[9px] text-slate-400">%</span>
          </div>
          {([['Offset X', 'shadowX', -40, 40], ['Offset Y', 'shadowY', -40, 40], ['Blur', 'shadowBlur', 0, 60], ['Spread', 'shadowSpread', -20, 20]] as const).map(([label, field, min, max]) => (
            <div key={field} className="flex items-center gap-2">
              <label className="text-[9px] text-slate-400 font-bold w-14 shrink-0">{label}</label>
              <input type="range" min={min} max={max} value={(el as any)[field] ?? 0}
                onChange={(e) => updateElementProps(el.id, { [field]: parseInt(e.target.value) })}
                onMouseUp={commitCurrentState}
                className="flex-1 accent-blue-600 h-1.5 rounded-lg cursor-pointer" />
              <input type="number" value={(el as any)[field] ?? 0} min={min} max={max}
                onChange={(e) => updateElementProps(el.id, { [field]: parseInt(e.target.value) })} onBlur={commitCurrentState}
                className="w-9 bg-white border border-slate-200 rounded px-1 py-0.5 text-[10px] text-center font-mono" />
            </div>
          ))}
        </div>
      )}
    </div>

    {/* ── BLEND MODE ── */}
    <div className="bg-slate-50 p-2.5 rounded border border-slate-200 space-y-2">
      <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 block">Blend Mode</span>
      <select value={el.blendMode || 'normal'} onChange={(e) => save({ blendMode: e.target.value })}
        className="w-full bg-white border border-slate-200 rounded p-1.5 text-[10px] font-semibold text-slate-700">
        {['normal','multiply','screen','overlay','darken','lighten','color-dodge','color-burn','hard-light','soft-light','difference','exclusion','hue','saturation','color','luminosity'].map(m => (
          <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1).replace('-', ' ')}</option>
        ))}
      </select>
    </div>

  </div>
)}

        {/* Universal: stacking, opacity, color, font */}
        <div className="border-t border-slate-200 pt-3 space-y-3.5">

          {/* Z-order */}
          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1.5">Stacking Order</span>
            <div className="grid grid-cols-4 gap-1.5">
              {(['front', 'up', 'down', 'back'] as const).map(action => (
                <button key={action} onClick={() => handleZIndex(el.id, action)}
                  className="py-1 bg-white border border-slate-200 text-slate-700 hover:bg-blue-50 hover:text-blue-600 rounded text-xs font-semibold shadow-xs transition-colors capitalize">
                  {action}
                </button>
              ))}
            </div>
          </div>

          {/* Opacity */}
          <div>
            <div className="flex justify-between items-center text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1">
              <span>Opacity:</span><span className="text-blue-600 font-bold">{el.opacity}%</span>
            </div>
            <input type="range" min="5" max="100" value={el.opacity}
              onChange={(e) => updateElementProps(el.id, { opacity: parseInt(e.target.value) })}
              onMouseUp={commitCurrentState}
              className="w-full accent-blue-600 h-1.5 bg-slate-200 rounded-lg cursor-pointer" />
          </div>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] uppercase tracking-wider text-slate-400 font-bold block mb-1">Ink / Text Color</label>
              <div className="flex gap-1.5 items-center bg-white p-1 rounded-lg border border-slate-200 shadow-sm" style={{ position: 'relative', zIndex: 9999 }}>
  <input type="color" value={el.color || '#000000'} onChange={(e) => updateElementProps(el.id, { color: e.target.value })} onBlur={commitCurrentState}
    className="bg-transparent border-none w-7 h-7 cursor-pointer" />
  <span className="font-mono text-[10px] pr-1 text-slate-800 font-semibold">{el.color || '#000'}</span>
</div>
              <div className="mt-1.5">
                <div className="flex justify-between text-[9px] text-slate-400 font-bold mb-0.5">
                  <span>Color Intensity</span>
                  <span className="text-blue-600">{Math.round((el.opacity ?? 100))}%</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] text-slate-300 font-bold">Light</span>
                  <input type="range" min="10" max="100" value={el.opacity ?? 100}
                    onChange={(e) => updateElementProps(el.id, { opacity: parseInt(e.target.value) })}
                    onMouseUp={commitCurrentState}
                    className="flex-1 accent-blue-600 h-1.5 rounded-lg cursor-pointer"
                    style={{background: `linear-gradient(to right, #e2e8f0, ${el.color || '#000000'})`}}
                  />
                  <span className="text-[9px] text-slate-700 font-bold">Dark</span>
                </div>
              </div>
            </div>
            {(el.type === 'shape' || el.type === 'table') && (
              <div>
                <label className="text-[9px] uppercase tracking-wider text-slate-400 font-bold block mb-1">Fill Color</label>
                <div className="flex gap-1.5 items-center bg-white p-1 rounded-lg border border-slate-200 shadow-sm" style={{ position: 'relative', zIndex: 9999 }}>
  <input type="color" value={el.backgroundColor === 'transparent' ? '#ffffff' : (el.backgroundColor || '#ffffff')}
    onChange={(e) => updateElementProps(el.id, { backgroundColor: e.target.value })} onBlur={commitCurrentState}
    className="bg-transparent border-none w-7 h-7 cursor-pointer" />
  <span className="font-mono text-[10px] pr-1 text-slate-800 font-semibold">{el.backgroundColor || 'transparent'}</span>
</div>
              </div>
            )}
          </div>

          {/* Typography */}
          {['text', 'stamp', 'signature', 'serial'].includes(el.type) && (
            <div className="space-y-2 pt-1">
              <div>
                <label className="text-[9px] uppercase tracking-wider text-slate-400 font-bold block mb-1">Font Size</label>
                <div className="flex gap-2 items-center bg-slate-50 p-1 rounded-lg border border-slate-200">
                  <button onClick={() => save({ fontSize: Math.max(6, (el.fontSize || 14) - 1) })}
                    className="w-7 h-7 bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 flex items-center justify-center rounded-md font-semibold text-sm shadow-sm">-</button>
                  <input type="number" value={el.fontSize || 14} min={6} max={100}
                    onChange={(e) => updateElementProps(el.id, { fontSize: parseInt(e.target.value) || 12 })} onBlur={commitCurrentState}
                    className="w-12 bg-transparent text-slate-800 font-mono text-center font-bold text-xs select-all focus:outline-none" />
                  <button onClick={() => save({ fontSize: Math.min(100, (el.fontSize || 14) + 1) })}
                    className="w-7 h-7 bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 flex items-center justify-center rounded-md font-semibold text-sm shadow-sm">+</button>
                </div>
              </div>

              <div>
                <label className="text-[9px] uppercase tracking-wider text-slate-400 font-bold block mb-1">Alignment</label>
                <div className="grid grid-cols-3 bg-white rounded-lg border border-slate-200 text-center font-semibold text-[11px] p-1 shadow-sm gap-1 h-[32px]">
                  {(['left', 'center', 'right'] as const).map(align => (
                    <button key={align} onClick={() => save({ textAlign: align })}
                      className={`rounded-md flex items-center justify-center transition-all capitalize ${el.textAlign === align ? 'bg-blue-600 text-white shadow-sm font-bold' : 'text-slate-600 hover:bg-slate-100'}`}>
                      {align}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[9px] uppercase tracking-wider text-slate-400 font-bold block mb-1">Font Family</label>
                <select value={el.fontFamily || 'Inter'} onChange={(e) => save({ fontFamily: e.target.value })}
                  className="w-full bg-white border border-slate-200 rounded p-1.5 text-slate-800 focus:outline-none text-xs font-medium">
                  <option value="Inter">Inter (Swiss Modern Sans)</option>
<option value="Space Grotesk">Space Grotesk (Tech)</option>
<option value="JetBrains Mono">JetBrains Mono (Console)</option>
<option value="Times New Roman">Times New Roman (Serif)</option>
<option value="Courier New">Courier New (Typewriter)</option>
<option value="Georgia">Georgia (Classic Serif)</option>
<option value="Playfair Display">Playfair Display (Elegant)</option>
<option value="Roboto">Roboto (Clean Sans)</option>
<option value="Lato">Lato (Friendly Sans)</option>
<option value="Montserrat">Montserrat (Modern Bold)</option>
<option value="Poppins">Poppins (Rounded Sans)</option>
<option value="Raleway">Raleway (Thin & Light)</option>
<option value="Oswald">Oswald (Condensed Bold)</option>
<option value="Merriweather">Merriweather (News Serif)</option>
<option value="Nunito">Nunito (Soft Rounded)</option>
<option value="Source Code Pro">Source Code Pro (Dev Mono)</option>
<option value="Ubuntu">Ubuntu (Linux Clean)</option>
<option value="Fira Sans">Fira Sans (Mozilla Sans)</option>
<option value="DM Sans">DM Sans (Modern UI)</option>
<option value="Barlow">Barlow (Condensed Clean)</option>
<option value="Libre Baskerville">Libre Baskerville (Book Serif)</option>
<option value="PT Serif">PT Serif (News Serif)</option>
<option value="PT Sans">PT Sans (News Sans)</option>
<option value="Inconsolata">Inconsolata (Code Mono)</option>
<option value="Work Sans">Work Sans (Office Clean)</option>
<option value="Mulish">Mulish (Geometric Sans)</option>
<option value="Quicksand">Quicksand (Bubbly Round)</option>
<option value="Josefin Sans">Josefin Sans (Art Deco)</option>
<option value="Cabin">Cabin (Humanist Sans)</option>
<option value="Crimson Text">Crimson Text (Book Oldstyle)</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <button onClick={() => save({ fontWeight: el.fontWeight === 'bold' ? 'normal' : 'bold' })}
                  className={`py-1 rounded font-bold border transition-all ${el.fontWeight === 'bold' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                  Bold
                </button>
                <button onClick={() => save({ fontStyle: el.fontStyle === 'italic' ? 'normal' : 'italic' })}
                  className={`py-1 rounded italic border transition-all ${el.fontStyle === 'italic' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                  Italic
                </button>
              </div>

              {/* Rotation */}
              <div>
                <div className="flex justify-between items-center text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1">
                  <span>Rotation:</span><span className="font-mono">{el.rotation || 0}°</span>
                </div>
                <input type="range" min="-180" max="180" step="1" value={el.rotation || 0}
                  onChange={(e) => updateElementProps(el.id, { rotation: parseInt(e.target.value) || 0 })}
                  onMouseUp={commitCurrentState}
                  className="w-full accent-blue-600 h-1.5 bg-slate-200 rounded-lg cursor-pointer" />
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ─── TableSettings (keeps table logic self-contained) ─────────────────────────

interface TableSettingsProps {
  el: CanvasElement;
  elements: CanvasElement[];
  pageSize: PageSizePreset;
  updateElementProps: (id: string, props: Partial<CanvasElement>) => void;
  commitCurrentState: () => void;
  saveToHistory: (els: CanvasElement[], ps: PageSizePreset) => void;
  computeTableTotals: (el: CanvasElement) => any;
  activeIndustryGroup: string;
  activeIndustryName: string;
  getFinancialSuggestions: (group: string) => any[];
  logs: any[];
  setLogs: (v: any) => void;
  addLogItem: (logs: any[], action: string, details: string) => any[];
  draggedColumnIdx: number | null;
  setDraggedColumnIdx: (v: number | null) => void;
  draggedOverColumnIdx: number | null;
  setDraggedOverColumnIdx: (v: number | null) => void;
  isSerialNoColumn: (col: any) => boolean;
}

const TableSettings: React.FC<TableSettingsProps> = ({
  el, elements, pageSize,
  updateElementProps, commitCurrentState, saveToHistory,
  computeTableTotals, activeIndustryGroup, activeIndustryName,
  getFinancialSuggestions, logs, setLogs, addLogItem,
  draggedColumnIdx, setDraggedColumnIdx, draggedOverColumnIdx, setDraggedOverColumnIdx,
  isSerialNoColumn,
}) => {
  const [showTableScanner, setShowTableScanner] = useState(false);
  const save = (props: Partial<CanvasElement>) => {
    updateElementProps(el.id, props);
    saveToHistory(elements.map(e => e.id === el.id ? { ...e, ...props } : e), pageSize);
  };

  return (
    <div className="space-y-4">

      {/* Columns manager */}
      <div className="bg-slate-50 p-2.5 rounded border border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <span className="font-bold text-slate-700 text-xs">Columns Layout</span>
          <div className="flex items-center gap-1">
            <button onClick={() => {
              if (!el.columns) return;
              if (el.columns.some(isSerialNoColumn)) return;
              const newCol: ColumnDefinition = { id: `col-${Date.now()}`, header: 'S.No.', width: 10, align: 'center', key: 'sno' };
              const scale = (100 - 10) / el.columns.reduce((s, c) => s + c.width, 0);
              const updatedCols = [newCol, ...el.columns.map(c => ({ ...c, width: Math.max(5, Math.round(c.width * scale)) }))];
              save({ columns: updatedCols });
            }}
              className="px-2 py-0.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded border border-emerald-200 text-[10px] flex items-center gap-1 cursor-pointer font-bold">
              🔢 Add S.No.
            </button>
            <button onClick={() => {
              if (!el.columns) return;
              const newColId = `col-${Date.now()}`;
              save({ columns: [...el.columns, { id: newColId, header: 'New Col', width: 15, align: 'right', key: newColId }] });
            }}
              className="px-2 py-0.5 bg-white text-slate-650 hover:bg-slate-100 rounded border border-slate-200 text-[10px] flex items-center gap-1 cursor-pointer">
              <Plus className="w-3 h-3" /> Add Col
            </button>
          </div>
        </div>

        <div className="space-y-1 max-h-[140px] overflow-y-auto pr-1">
          {el.columns?.map((col, cIdx) => (
            <div key={col.id}
              draggable
              onDragStart={() => setDraggedColumnIdx(cIdx)}
              onDragOver={(e) => e.preventDefault()}
              onDragEnter={() => setDraggedOverColumnIdx(cIdx)}
              onDrop={() => {
                if (draggedColumnIdx === null || draggedColumnIdx === cIdx) return;
                const cols = [...el.columns!];
                const [moved] = cols.splice(draggedColumnIdx, 1);
                cols.splice(cIdx, 0, moved);
                save({ columns: cols });
                setDraggedColumnIdx(null); setDraggedOverColumnIdx(null);
              }}
              onDragEnd={() => { setDraggedColumnIdx(null); setDraggedOverColumnIdx(null); }}
              className={`flex items-center justify-between text-[11px] bg-white p-1.5 rounded border gap-1 shadow-xs transition-all ${
                draggedColumnIdx === cIdx ? 'opacity-30 border-dashed border-blue-500' :
                draggedOverColumnIdx === cIdx ? 'border-blue-500 bg-blue-50/40 ring-2 ring-blue-500/20' :
                'border-slate-200 hover:border-blue-200'
              }`}>
              <div className="cursor-grab text-slate-400 hover:text-slate-600 px-0.5"><GripVertical className="w-3.5 h-3.5" /></div>
              <div className="flex flex-col flex-1 min-w-0 gap-0.5">
  <input type="text" value={col.header}
    onChange={(e) => updateElementProps(el.id, { columns: el.columns!.map(c => c.id === col.id ? { ...c, header: e.target.value } : c) })}
    onBlur={commitCurrentState}
    className="bg-transparent border-none text-slate-800 font-bold outline-none focus:ring-1 focus:ring-blue-500 rounded px-1.5 py-0.5 text-[10.5px] w-full" />
  <select
    value={col.colType || 'text'}
    onChange={(e) => {
      const colType = e.target.value;
      updateElementProps(el.id, {
        columns: el.columns!.map(c => c.id === col.id ? {
          ...c,
          colType,
          ...(colType === 'serial' ? { serialStart: 1, serialPrefix: '', serialSuffix: '' } : {})
        } : c)
      });
      commitCurrentState();
    }}
    className="bg-slate-50 border border-slate-200 rounded text-[9px] font-bold text-slate-500 px-1 py-0.5 focus:outline-none focus:border-blue-400 w-full"
  >
    <option value="text">Text</option>
    <option value="serial">🔢 Serial / Auto #</option>
    <option value="currency">💰 Currency</option>
    <option value="number">Number</option>
    <option value="date">Date</option>
  </select>

  {/* Serial config inline */}
  {col.colType === 'serial' && (
    <div className="flex gap-1 mt-0.5">
      <input
        type="text"
        placeholder="Prefix"
        value={col.serialPrefix || ''}
        onChange={(e) => updateElementProps(el.id, {
          columns: el.columns!.map(c => c.id === col.id ? { ...c, serialPrefix: e.target.value } : c)
        })}
        onBlur={commitCurrentState}
        className="w-12 bg-white border border-slate-200 rounded px-1 py-0.5 text-[9px] font-mono text-slate-700 focus:outline-none focus:border-blue-400"
      />
      <input
        type="number"
        placeholder="Start"
        value={col.serialStart ?? 1}
        onChange={(e) => updateElementProps(el.id, {
          columns: el.columns!.map(c => c.id === col.id ? { ...c, serialStart: parseInt(e.target.value) || 1 } : c)
        })}
        onBlur={commitCurrentState}
        className="w-10 bg-white border border-slate-200 rounded px-1 py-0.5 text-[9px] font-mono text-slate-700 focus:outline-none focus:border-blue-400"
      />
      <input
        type="text"
        placeholder="Suffix"
        value={col.serialSuffix || ''}
        onChange={(e) => updateElementProps(el.id, {
          columns: el.columns!.map(c => c.id === col.id ? { ...c, serialSuffix: e.target.value } : c)
        })}
        onBlur={commitCurrentState}
        className="w-12 bg-white border border-slate-200 rounded px-1 py-0.5 text-[9px] font-mono text-slate-700 focus:outline-none focus:border-blue-400"
      />
    </div>
  )}
</div>
              <div className="flex items-center gap-1 text-[10px] text-slate-500 shrink-0">
                <span>W:</span>
                <input type="number" value={col.width}
                  onChange={(e) => updateElementProps(el.id, { columns: el.columns!.map(c => c.id === col.id ? { ...c, width: parseInt(e.target.value) || 10 } : c) })}
                  onBlur={commitCurrentState}
                  className="w-8 bg-slate-50 border border-slate-200 rounded text-center font-semibold" />
                <span>%</span>
              </div>
              <div className="flex flex-col gap-0.5 shrink-0">
                <button disabled={cIdx === 0} onClick={() => {
                  const cols = [...el.columns!]; [cols[cIdx], cols[cIdx-1]] = [cols[cIdx-1], cols[cIdx]]; save({ columns: cols });
                }} className="text-slate-400 hover:text-blue-600 disabled:opacity-20 cursor-pointer disabled:cursor-not-allowed"><ArrowUp className="w-3 h-3" /></button>
                <button disabled={cIdx === el.columns!.length - 1} onClick={() => {
                  const cols = [...el.columns!]; [cols[cIdx], cols[cIdx+1]] = [cols[cIdx+1], cols[cIdx]]; save({ columns: cols });
                }} className="text-slate-400 hover:text-blue-600 disabled:opacity-20 cursor-pointer disabled:cursor-not-allowed"><ArrowDown className="w-3 h-3" /></button>
              </div>
              {col.key !== 'description' ? (
                <button onClick={() => save({ columns: el.columns!.filter(c => c.id !== col.id) })} className="text-slate-400 hover:text-red-500 p-0.5">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              ) : <div className="w-4 shrink-0" />}
            </div>
          ))}
        </div>
      </div>

      {/* Row editor */}
      <div className="bg-slate-50 p-2.5 rounded border border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <span className="font-bold text-slate-705 text-xs">Invoice Items</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setShowTableScanner(true)}
              className="px-2 py-0.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white rounded border border-emerald-200 text-[10px] flex items-center gap-1 cursor-pointer font-bold transition-all">
              📷 Scan
            </button>
            <button onClick={() => {
              if (!el.rows) return;
              const newRow = { id: `row-${Date.now()}`, values: { description: 'New service line item', qty: 1, rate: 100, total: 100 } };
              save({ rows: [...el.rows, newRow] });
            }}
              className="px-2 py-0.5 bg-white text-slate-650 hover:bg-slate-100 rounded border border-slate-200 text-[10px] flex items-center gap-1 shadow-sm">
              <Plus className="w-3 h-3" /> Add Row
            </button>
          </div>
        </div>
        {showTableScanner && (
          <BarcodeScannerModal
            onDetected={async (val) => {
              const productInfo = await lookupBarcode(val);
              const newRow = {
                id: `row-${Date.now()}`,
                values: {
                  description: productInfo
                    ? `${productInfo.name}${productInfo.brand ? ' — ' + productInfo.brand : ''}`
                    : val,
                  qty: 1,
                  rate: productInfo?.rate || 0,
                  total: productInfo?.rate || 0,
                }
              };
              save({ rows: [...(el.rows || []), newRow] });
              setShowTableScanner(false);
            }}
            onClose={() => setShowTableScanner(false)}
          />
        )}
        <div className="space-y-1.5 max-h-[140px] overflow-y-auto pr-1">
          {el.rows?.map((row, rIdx) => (
            <div key={row.id} className="bg-white p-2 rounded border border-slate-200 space-y-1 shadow-xs">
              <div className="flex items-center justify-between text-[10px] text-slate-400 border-b border-slate-100 pb-1 mb-1 font-medium">
                <span>Row #{rIdx + 1}</span>
                <button onClick={() => save({ rows: el.rows!.filter(r => r.id !== row.id) })} className="text-slate-400 hover:text-red-500 font-semibold">Remove</button>
              </div>
              <input type="text" value={String(row.values.description || '')}
                onChange={(e) => updateElementProps(el.id, { rows: el.rows!.map(r => r.id === row.id ? { ...r, values: { ...r.values, description: e.target.value } } : r) })}
                onBlur={commitCurrentState}
                className="w-full bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-slate-800 outline-none font-medium" placeholder="Description" />
              <div className="grid grid-cols-2 gap-1">
                {[{ label: 'Qty', key: 'qty' }, { label: 'Rate', key: 'rate' }].map(({ label, key }) => (
                  <div key={key} className="flex items-center gap-1">
                    <span className="text-[9px] text-slate-400 font-bold">{label}:</span>
                    <input type="number" value={Number(row.values[key]) || 0}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        updateElementProps(el.id, { rows: el.rows!.map(r => {
                          if (r.id !== row.id) return r;
                          const qty = key === 'qty' ? val : parseFloat(String(r.values.qty)) || 0;
                          const rate = key === 'rate' ? val : parseFloat(String(r.values.rate)) || 0;
                          return { ...r, values: { ...r.values, [key]: val, total: qty * rate } };
                        })});
                      }}
                      onBlur={commitCurrentState}
                      className="w-full bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-slate-800 font-mono" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

   {/* Dynamic Taxes */}
<div className="space-y-1.5">
  <div className="flex items-center justify-between">
    <label className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">Taxes</label>
    <button
      onClick={() => {
        const current = (el as any).taxes || [];
        updateElementProps(el.id, { taxes: [...current, { label: 'Tax', rate: 0 }] } as any);
        commitCurrentState();
      }}
      className="text-[9px] text-blue-500 hover:text-blue-700 font-bold flex items-center gap-0.5 cursor-pointer"
    >
      + Add Tax
    </button>
  </div>

  {((el as any).taxes || []).map((tax: any, idx: number) => (
  <div key={idx} className="bg-slate-50 p-2 rounded border border-slate-200">
    <div className="flex items-center justify-between mb-1">
      <span className="text-[9px] text-slate-400 font-bold uppercase">Tax {idx + 1}</span>
      <button
        onClick={() => {
          const taxes = (el as any).taxes.filter((_: any, i: number) => i !== idx);
          updateElementProps(el.id, { taxes } as any);
          commitCurrentState();
        }}
        className="text-[9px] text-red-400 hover:text-red-600 font-bold cursor-pointer"
      >✕ Remove</button>
    </div>
    <div className="flex gap-1.5 items-center">
        <input
          type="text"
          placeholder="e.g. VAT, GST"
          value={tax.label}
          onChange={(e) => {
            const taxes = [...(el as any).taxes];
            taxes[idx] = { ...taxes[idx], label: e.target.value };
            updateElementProps(el.id, { taxes } as any);
          }}
          onBlur={commitCurrentState}
          className="flex-1 bg-white border border-slate-200 rounded p-1 text-slate-700 text-xs focus:outline-none focus:border-blue-400"
        />
        <input
          type="number"
          value={tax.rate}
          onChange={(e) => {
            const taxes = [...(el as any).taxes];
            taxes[idx] = { ...taxes[idx], rate: parseFloat(e.target.value) || 0 };
            updateElementProps(el.id, { taxes } as any);
          }}
          onBlur={commitCurrentState}
          className="w-14 bg-white border border-slate-200 rounded p-1 text-slate-700 font-mono text-center text-xs focus:outline-none"
        />
        <span className="text-slate-400 text-xs">%</span>
        <button
          onClick={() => {
            const taxes = (el as any).taxes.filter((_: any, i: number) => i !== idx);
            updateElementProps(el.id, { taxes } as any);
            commitCurrentState();
          }}
          className="w-6 h-6 flex items-center justify-center rounded bg-red-50 hover:bg-red-500 hover:text-white text-red-400 text-xs font-bold transition-all shrink-0 border border-red-100 cursor-pointer"
        >✕</button>
      </div>
    </div>
  ))}
</div>

{/* Dynamic Discounts */}
<div className="space-y-1.5">
  <div className="flex items-center justify-between">
    <label className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">Discounts</label>
    <button
      onClick={() => {
        const current = (el as any).discounts || [];
        updateElementProps(el.id, { discounts: [...current, { label: 'Discount', rate: 0 }] } as any);
        commitCurrentState();
      }}
      className="text-[9px] text-blue-500 hover:text-blue-700 font-bold flex items-center gap-0.5 cursor-pointer"
    >
      + Add Discount
    </button>
  </div>

  {((el as any).discounts || []).map((disc: any, idx: number) => (
  <div key={idx} className="bg-slate-50 p-2 rounded border border-slate-200">
    <div className="flex items-center justify-between mb-1">
      <span className="text-[9px] text-slate-400 font-bold uppercase">Discount {idx + 1}</span>
      <button
        onClick={() => {
          const discounts = (el as any).discounts.filter((_: any, i: number) => i !== idx);
          updateElementProps(el.id, { discounts } as any);
          commitCurrentState();
        }}
        className="text-[9px] text-red-400 hover:text-red-600 font-bold cursor-pointer"
      >✕ Remove</button>
    </div>
    <div className="flex gap-1.5 items-center">
        <input
          type="text"
          placeholder="e.g. Loyalty, Promo"
          value={disc.label}
          onChange={(e) => {
            const discounts = [...(el as any).discounts];
            discounts[idx] = { ...discounts[idx], label: e.target.value };
            updateElementProps(el.id, { discounts } as any);
          }}
          onBlur={commitCurrentState}
          className="flex-1 bg-white border border-slate-200 rounded p-1 text-slate-700 text-xs focus:outline-none focus:border-blue-400"
        />
        <input
          type="number"
          value={disc.rate}
          onChange={(e) => {
            const discounts = [...(el as any).discounts];
            discounts[idx] = { ...discounts[idx], rate: parseFloat(e.target.value) || 0 };
            updateElementProps(el.id, { discounts } as any);
          }}
          onBlur={commitCurrentState}
          className="w-14 bg-white border border-slate-200 rounded p-1 text-slate-700 font-mono text-center text-xs focus:outline-none"
        />
        <span className="text-slate-400 text-xs">%</span>
        <button
          onClick={() => {
            const discounts = (el as any).discounts.filter((_: any, i: number) => i !== idx);
            updateElementProps(el.id, { discounts } as any);
            commitCurrentState();
          }}
          className="w-6 h-6 flex items-center justify-center rounded bg-red-50 hover:bg-red-500 hover:text-white text-red-400 text-xs font-bold transition-all shrink-0 border border-red-100 cursor-pointer"
        >✕</button>
      </div>
    </div>
  ))}
</div>
      <button onClick={() => {
        updateElementProps(el.id, { taxes: [{ label: 'UAE VAT', rate: 5 }] } as any);
        commitCurrentState();
      }}
        className="w-full py-1 text-[9px] font-black uppercase tracking-wider rounded-md border border-emerald-200 bg-emerald-50/20 text-emerald-800 hover:bg-emerald-50 cursor-pointer text-center flex items-center justify-center gap-1.5">
        🇦🇪 Auto-Apply UAE 5% VAT
      </button>

      {/* Smart Financial Suggestions */}
      <div className="bg-gradient-to-br from-blue-50/70 to-slate-50 border border-blue-100 rounded-lg p-2.5 space-y-2 text-xs">
        <div className="flex items-center gap-1.5 border-b border-blue-100 pb-1.5">
          <Palette className="w-3.5 h-3.5 text-blue-600 shrink-0" />
          <span className="font-extrabold text-[11px] text-slate-800">Smart Financial Settings</span>
          <span className="text-[8px] px-2 py-0.5 bg-blue-100/80 text-purple-900 rounded-full font-bold uppercase shrink-0">{activeIndustryGroup}</span>
        </div>
        <div className="space-y-1.5 max-h-[148px] overflow-y-auto pr-1">
          {getFinancialSuggestions(activeIndustryGroup).map((sug, sIdx) => (
            <div key={sIdx} className="p-1.5 bg-white border border-slate-200 rounded-md hover:border-blue-200 transition-all flex flex-col gap-1 group">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[9.5px] font-bold text-slate-800 leading-tight">{sug.taxLabel} / {sug.discountLabel}</span>
                <button onClick={() => {
                  const updates = {
                    taxes: sug.taxRate > 0 ? [{ label: sug.taxLabel, rate: sug.taxRate }] : [],
                    discounts: sug.discountRate > 0 ? [{ label: sug.discountLabel, rate: sug.discountRate }] : [],
                  };
                  updateElementProps(el.id, updates as any);
                  saveToHistory(elements.map(e => e.id === el.id ? { ...e, ...updates } : e), pageSize);
                  setLogs(addLogItem(logs, 'FINANCIAL_PRESET_APPLIED', `Applied ${activeIndustryGroup}: Tax ${sug.taxRate}%, Discount ${sug.discountRate}%`));
                }}
                  className="px-1.5 py-0.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-[8.5px] font-bold cursor-pointer shadow-xs">
                  Apply
                </button>
              </div>
              <p className="text-[8.5px] text-slate-450 italic">{sug.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Country Tax Picker */}
      <CountryTaxPicker el={el} updateElementProps={updateElementProps} commitCurrentState={commitCurrentState} elements={elements} pageSize={pageSize} saveToHistory={saveToHistory} />

      {/* Custom Presets */}
      <CustomTaxPresets el={el} updateElementProps={updateElementProps} commitCurrentState={commitCurrentState} elements={elements} pageSize={pageSize} saveToHistory={saveToHistory} />
      
    </div>
  );
};
